import { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';

interface Word {
  id: string;
  english_word: string;
  turkish_word: string;
  example_sentence: string;
  example_sentence_turkish: string;
  difficulty: number;
}

interface FavoriteProgressRecord {
  knownCount: number;
  unknownCount: number;
  lastReviewed: string | null;
}

type FavoriteProgressMap = Record<string, FavoriteProgressRecord>;

const storageKeyForFavorites = (userId: string) => `favorites_${userId}`;
const storageKeyForFavProgress = (userId: string) => `favorites_progress_${userId}`;

export const useFavorites = (userId: string | null, allWords: Word[]) => {
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const [progress, setProgress] = useState<FavoriteProgressMap>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [hasLoadedRemote, setHasLoadedRemote] = useState(false);

  const arraysEqual = (a: string[], b: string[]) => {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  };

  const progressEqual = (a: FavoriteProgressMap, b: FavoriteProgressMap) => {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
      const av = a[k];
      const bv = b[k];
      if (!bv) return false;
      if (
        av.knownCount !== bv.knownCount ||
        av.unknownCount !== bv.unknownCount ||
        av.lastReviewed !== bv.lastReviewed
      ) return false;
    }
    return true;
  };
  const syncToFirestore = async (ids: string[], prog: FavoriteProgressMap) => {
    if (!userId) return;
    try {
      setIsSyncing(true);
      const favRef = doc(db, 'user_favorites', userId);
      await setDoc(
        favRef,
        { ids, progress: prog, updatedAt: serverTimestamp() },
        { merge: true }
      );
      console.log('Favoriler Firebase\'e başarıyla kaydedildi:', ids.length, 'kelime');
    } catch (e) {
      console.error('Favori senkronizasyon hatası:', e);
      // Hata durumunda localStorage'a kaydet
      localStorage.setItem(storageKeyForFavorites(userId), JSON.stringify(ids));
      localStorage.setItem(storageKeyForFavProgress(userId), JSON.stringify(prog));
    } finally {
      setIsSyncing(false);
    }
  };

  // Real-time Firestore subscription with local fallback/migration
  useEffect(() => {
    if (!userId) return;
    const favRef = doc(db, 'user_favorites', userId);

    let unsub = onSnapshot(favRef, async (snap) => {
      try {
        if (snap.exists()) {
          const data = snap.data() as { ids?: string[]; progress?: FavoriteProgressMap };
          const remoteIds = Array.isArray(data.ids) ? data.ids : [];
          const remoteProg = data.progress || {};
          // Update only when different
          setFavoriteIds((prev) => (arraysEqual(prev, remoteIds) ? prev : remoteIds));
          setProgress((prev) => (progressEqual(prev, remoteProg) ? prev : remoteProg));
          localStorage.setItem(storageKeyForFavorites(userId), JSON.stringify(remoteIds));
          localStorage.setItem(storageKeyForFavProgress(userId), JSON.stringify(remoteProg));
          setHasLoadedRemote(true);
        } else {
          // If remote missing, migrate local if any; else create empty
          const localIds = JSON.parse(localStorage.getItem(storageKeyForFavorites(userId)) || '[]');
          const localProg = JSON.parse(localStorage.getItem(storageKeyForFavProgress(userId)) || '{}');
          if ((Array.isArray(localIds) && localIds.length) || Object.keys(localProg || {}).length) {
            await setDoc(favRef, { ids: localIds || [], progress: localProg || {}, updatedAt: serverTimestamp() }, { merge: true });
            setFavoriteIds(Array.isArray(localIds) ? localIds : []);
            setProgress(localProg || {});
          } else {
            await setDoc(favRef, { ids: [], progress: {}, updatedAt: serverTimestamp() }, { merge: true });
            setFavoriteIds([]);
            setProgress({});
          }
          setHasLoadedRemote(true);
        }
      } catch {
        // ignore
      }
    });

    return () => {
      if (unsub) unsub();
    };
  }, [userId]);

  // Persist to localStorage
  useEffect(() => {
    if (!userId || !hasLoadedRemote) return;
    localStorage.setItem(storageKeyForFavorites(userId), JSON.stringify(favoriteIds));
    // save to Firestore (best effort)
    syncToFirestore(favoriteIds, progress);
  }, [userId, favoriteIds, hasLoadedRemote]);

  useEffect(() => {
    if (!userId || !hasLoadedRemote) return;
    localStorage.setItem(storageKeyForFavProgress(userId), JSON.stringify(progress));
    syncToFirestore(favoriteIds, progress);
  }, [userId, progress, hasLoadedRemote]);

  const favoriteWords: Word[] = useMemo(() => {
    if (!favoriteIds || favoriteIds.length === 0) return [];
    const map: Record<string, Word> = Object.fromEntries(allWords.map(w => [w.id, w]));
    return favoriteIds
      .map(id => map[id])
      .filter((w): w is Word => Boolean(w));
  }, [favoriteIds, allWords]);

  const isFavorite = (wordId?: string | null) => {
    if (!wordId) return false;
    return favoriteIds.includes(wordId);
  };

  const toggleFavorite = (wordId: string) => {
    setFavoriteIds(prev => {
      const next = prev.includes(wordId) ? prev.filter(id => id !== wordId) : [...prev, wordId];
      // Always try to sync to Firebase, regardless of hasLoadedRemote
      syncToFirestore(next, progress);
      return next;
    });
  };

  const getCurrent = () => {
    if (favoriteWords.length === 0) return null;
    const safe = Math.max(0, Math.min(currentIndex, favoriteWords.length - 1));
    return favoriteWords[safe];
  };

  const nextFavorite = () => {
    if (favoriteWords.length === 0) return;
    setCurrentIndex(i => (i + 1) % favoriteWords.length);
  };

  const markFavoriteKnown = (wordId: string) => {
    const now = new Date().toISOString();
    setProgress(prev => {
      const next = {
        ...prev,
        [wordId]: {
          knownCount: (prev[wordId]?.knownCount || 0) + 1,
          unknownCount: prev[wordId]?.unknownCount || 0,
          lastReviewed: now,
        },
      } as FavoriteProgressMap;
      // Always try to sync to Firebase
      syncToFirestore(favoriteIds, next);
      return next;
    });
  };

  const markFavoriteUnknown = (wordId: string) => {
    const now = new Date().toISOString();
    setProgress(prev => {
      const next = {
        ...prev,
        [wordId]: {
          knownCount: prev[wordId]?.knownCount || 0,
          unknownCount: (prev[wordId]?.unknownCount || 0) + 1,
          lastReviewed: now,
        },
      } as FavoriteProgressMap;
      // Always try to sync to Firebase
      syncToFirestore(favoriteIds, next);
      return next;
    });
  };

  return {
    favoriteIds,
    favoriteWords,
    isFavorite,
    toggleFavorite,
    getCurrent,
    nextFavorite,
    markFavoriteKnown,
    markFavoriteUnknown,
    syncFavorites: () => syncToFirestore(favoriteIds, progress),
    isSyncing,
    hasLoadedRemote,
  };
};


